import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { exerciseAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ExerciseVisual3DModels from '../components/ExerciseVisual3DModels';
import {
  ArrowLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  PlayIcon,
  CheckCircleIcon,
  StarIcon,
  HeartIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const ExerciseDetail = () => {
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(0);

  const { data: exercise, isLoading } = useQuery(
    ['exercise', id],
    () => exerciseAPI.getExercise(id)
  );

  if (isLoading) {
    return <LoadingSpinner text="Loading exercise details..." />;
  }

  if (!exercise?.data) {
    return (
      <div className="p-6">
        <div className="text-center py-16">
          <h3 className="text-xl font-bold text-gray-900">Exercise not found</h3>
          <p className="text-gray-600 mt-2">The exercise you're looking for doesn't exist.</p>
          <Link to="/exercises" className="btn btn-primary mt-4">
            Back to Exercises
          </Link>
        </div>
      </div>
    );
  }

  const exerciseData = exercise.data;

  const difficultyColors = {
    1: 'from-green-400 to-green-500',
    2: 'from-blue-400 to-blue-500', 
    3: 'from-yellow-400 to-yellow-500',
    4: 'from-orange-400 to-orange-500',
    5: 'from-red-400 to-red-500'
  };

  const difficultyIcons = {
    1: '🌱',
    2: '🌿', 
    3: '🌸',
    4: '🔥',
    5: '⚡'
  };

  const categoryIcons = {
    'Basic Obedience': '🎯',
    'Agility': '🏃‍♂️',
    'Tricks': '🎪',
    'Socialization': '👥',
    'Behavioral': '🧠',
    'Mental Stimulation': '🧩',
    'Exercise & Fitness': '💪',
    'Grooming & Care': '🛁'
  };

  // Parse instructions into steps
  const instructionSteps = exerciseData.instructions
    .split('. ')
    .filter(step => step.trim())
    .map(step => step.trim().replace(/\.$/, ''));

  const tips = exerciseData.tips?.split('. ').filter(tip => tip.trim()) || [];
  const safetyNotes = exerciseData.safety_notes?.split('. ').filter(note => note.trim()) || [];

  return (
    <div className="p-6 min-h-screen">
      {/* Back Button */}
      <div className="mb-6">
        <Link
          to="/app/exercises"
          className="inline-flex items-center text-playful-blue hover:text-playful-green transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Exercises
        </Link>
      </div>

      {/* Exercise Header */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="text-6xl">
              {categoryIcons[exerciseData.category_name] || '🎯'}
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {exerciseData.name}
              </h1>
              <p className="text-xl text-playful-blue font-medium">
                {exerciseData.category_name}
              </p>
            </div>
          </div>
          
          <div className={`
            px-4 py-2 rounded-full text-white text-lg font-bold 
            bg-gradient-to-r ${difficultyColors[exerciseData.difficulty_level]}
            flex items-center space-x-2
          `}>
            <span>{difficultyIcons[exerciseData.difficulty_level]}</span>
            <span>{exerciseData.difficulty_name}</span>
          </div>
        </div>

        <p className="text-gray-700 text-lg mb-6">{exerciseData.description}</p>

        {/* Exercise Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <ClockIcon className="h-5 w-5 text-playful-blue" />
              <span className="font-bold text-gray-700">Duration</span>
            </div>
            <p className="text-2xl font-bold text-playful-blue">{exerciseData.duration_minutes} minutes</p>
          </div>
          
          <div className="bg-gradient-to-r from-accent-50 to-accent-100 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <StarIcon className="h-5 w-5 text-playful-orange" />
              <span className="font-bold text-gray-700">Difficulty</span>
            </div>
            <p className="text-2xl font-bold text-playful-orange">Level {exerciseData.difficulty_level}</p>
          </div>
          
          <div className="bg-gradient-to-r from-secondary-50 to-secondary-100 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <HeartIcon className="h-5 w-5 text-playful-green" />
              <span className="font-bold text-gray-700">Equipment</span>
            </div>
            <p className="text-lg font-bold text-playful-green">
              {exerciseData.equipment_needed || 'None needed! 🎉'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Training Steps with Visual */}
        <div className="lg:col-span-2 space-y-8">
          {/* Visual Demonstration */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex items-center space-x-3 mb-6">
              <PlayIcon className="h-8 w-8 text-playful-blue" />
              <h2 className="text-2xl font-bold text-gray-900">3D Visual Demo</h2>
              <SparklesIcon className="h-8 w-8 text-playful-green animate-wiggle" />
            </div>
            
            <ExerciseVisual3DModels 
              exerciseName={exerciseData.name}
              step={currentStep}
              stepText={instructionSteps[currentStep]}
              animationData={exerciseData.animation_data}
              onStepChange={setCurrentStep}
              totalSteps={instructionSteps.length}
              className="mb-4"
            />
            
            <div className="text-center">
              <p className="text-sm text-gray-600">
                👆 Click on training steps below to see the demonstration! • 🖱️ Drag to rotate the 3D view!
              </p>
            </div>
          </div>

          {/* Training Steps */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex items-center space-x-3 mb-6">
              <PlayIcon className="h-8 w-8 text-playful-blue" />
              <h2 className="text-2xl font-bold text-gray-900">Step-by-Step Training</h2>
              <SparklesIcon className="h-8 w-8 text-playful-green animate-wiggle" />
            </div>

            <div className="space-y-6">
              {instructionSteps.map((step, index) => (
                <div
                  key={index}
                  className={`
                    p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer
                    ${currentStep === index
                      ? 'border-playful-blue bg-gradient-to-r from-primary-50 to-secondary-50 shadow-lg'
                      : 'border-gray-200 hover:border-playful-blue hover:bg-gray-50'
                    }
                  `}
                  onClick={() => setCurrentStep(index)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold text-white
                      ${currentStep === index
                        ? 'bg-gradient-to-r from-playful-blue to-playful-green animate-pulse'
                        : 'bg-gray-300'
                      }
                    `}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-800 text-lg leading-relaxed">{step}</p>
                    </div>
                    {currentStep === index && (
                      <CheckCircleIcon className="h-6 w-6 text-playful-green animate-bounce" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <div className="bg-gradient-to-r from-playful-blue to-playful-green rounded-xl p-4 text-white">
                <p className="text-lg font-bold">
                  🎉 Remember: Every small step counts! Keep sessions fun and positive! 🎉
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tips & Safety */}
        <div className="space-y-6">
          {/* Pro Tips */}
          {tips.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <LightBulbIcon className="h-6 w-6 text-yellow-500" />
                <h3 className="text-lg font-bold text-gray-900">Pro Tips 💡</h3>
              </div>
              <div className="space-y-3">
                {tips.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Safety Notes */}
          {safetyNotes.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
                <h3 className="text-lg font-bold text-gray-900">Safety First 🛡️</h3>
              </div>
              <div className="space-y-3">
                {safetyNotes.map((note, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Compatible Pets */}
          {exerciseData.compatibility && exerciseData.compatibility.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Perfect For 🐾</h3>
              <div className="space-y-3">
                {exerciseData.compatibility.map((comp, index) => (
                  <div key={index} className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-3">
                    <p className="font-medium text-gray-900">{comp.pet_type}s</p>
                    {comp.min_age_months && (
                      <p className="text-sm text-gray-600">Min age: {comp.min_age_months} months</p>
                    )}
                    {comp.min_weight_kg && (
                      <p className="text-sm text-gray-600">Min weight: {comp.min_weight_kg} kg</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Start Training Button */}
          <div className="bg-gradient-to-r from-playful-blue to-playful-green rounded-2xl p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">Ready to Start? 🚀</h3>
            <p className="mb-4 text-primary-100">
              Grab your treats and let's make some training magic happen!
            </p>
            <button className="w-full bg-white text-playful-blue font-bold py-3 px-4 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200">
              🎯 Begin Training Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetail;